import { OrderRequirementsDialog } from "../OrderDetails/OrderRequirementsDialog";
import { OrderInstructionsDialog } from "../OrderDetails/OrderInstructionsDialog";
import { useDriverOrderCompleteness } from "../DriverOrders/useDriverOrderCompleteness";
/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Inline } from "@design-system";
import {
  DialogNotify,
  OrderDetails,
  SplitTable,
  StatusBadge,
  EditDriver,
  DeleteOrderIcon,
  StartOrderIcon,
  EditRoute,
  UpdateStatus,
  SendDeliveryLetter,
  EditCargo,
  SplitTableRef,
  OrderTemplateSaveDialog,
} from "@components";
import { Customer, Order, OrderTemplate } from "../../models";
import { ITableRow, Timestamp, IOrder } from "../../types";
import { IRecurrenceRule } from "../../types/order/IOrderTemplate";
import { useCache } from "../../context/CacheContext";
import {
  HiChevronRight,
  HiOutlineChevronDown,
  HiOutlineDocumentText,
  HiOutlineClipboardCopy,
  HiOutlineLink,
} from "react-icons/hi";
import { Toast, Tooltip } from "flowbite-react";
import { DropdownButton } from "../Button/DropdownButton";
import { OrderStatus } from "../../types/order/IOrder";
import { useWorkspaceData, useDataCache } from "@data";
import { useWorkspace } from "../../context/WorkspaceContext";
import { cancelOrder as cancelOrderApi } from "../../api";
import { OrderSortMode, sortOrders } from "../../helpers/orderSorting";
import {
  buildPublicTrackingUrl,
  createOrderTrackingLink,
  isOrderTrackingEnabledForWorkspace,
} from "../../api/orderTracking";
import { WorkspaceRole } from "../../types/Workspace";

import { useUserSession } from "../../context/UserContext";
import { useNavigate, useLocation } from "react-router-dom";
import { HiCheck } from "react-icons/hi";
import {
  createOrderInvoice,
  assignOrder,
  completeOrder,
  startOrder,
  deleteOrderApi,
} from "../../api/client";

interface OrderListProp {
  orders: Order[];
  /** URL-selected order from the workspace store, independent of list pagination. */
  linkedOrder?: Order;
  isLoading: boolean;
  isOpen: (val: boolean) => void;
  customerSearchText?: string;
  /** Disable when the parent has filtered the full dataset before pagination. */
  applyLocalSearch?: boolean;
  onCustomerSearch?: (searchText: string) => void;
  preventAutoOpen?: boolean;
  tableFooter?: React.ReactNode;
  onOrderSelect?: (order: Order | undefined) => void; // New callback
  sortMode?: OrderSortMode;
}

export const OrderList: FC<OrderListProp> = function (props: OrderListProp) {
  const { t } = useTranslation(["common", "orders", "validation", "workspace"]);
  const completeness = useDriverOrderCompleteness(
    props.orders.map((order) => order.id),
  );
  const [openedOrder, setOpenedOrder] = useState<Order | undefined>(undefined);
  const [tableRows, setTableRows] = useState([] as ITableRow[]);
  const [hideColumns, setHideColumns] = useState(false);
  const [searchText, setSearchText] = useState<string>(
    props.customerSearchText || "",
  );
  const [searchResults, setSearchResults] = useState<Order[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [showStartOrderDialog, setShowStartOrderDialog] = useState(false);
  const [showEditDriver, setShowEditDriver] = useState(false);
  const [showEditRoute, setShowEditRoute] = useState(false);
  const [instructionsTarget, setInstructionsTarget] = useState<{
    orderId: string;
    workspaceId: string;
  }>();
  const [requirementsTarget, setRequirementsTarget] = useState<{
    orderId: string;
    workspaceId: string;
  }>();
  const [showEditCargo, setShowEditCargo] = useState(false);
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [showDeleteOrderDialog, setShowDeleteOrderDialog] = useState(false);
  const [showCancelOrderDialog, setShowCancelOrderDialog] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showTemplateSuccessToast, setShowTemplateSuccessToast] =
    useState(false);
  const [showDeliveryLetterDialog, setShowDeliveryLetterDialog] =
    useState(false);
  const [showInvoiceSuccessToast, setShowInvoiceSuccessToast] = useState(false);
  const [showCreateInvoiceDialog, setShowCreateInvoiceDialog] = useState(false);
  const [showInvoiceErrorToast, setShowInvoiceErrorToast] = useState(false);
  const [showActionErrorToast, setShowActionErrorToast] = useState(false);
  const [showTrackingLinkDialog, setShowTrackingLinkDialog] = useState(false);
  const [trackingUrl, setTrackingUrl] = useState("");
  const [trackingLinkCopied, setTrackingLinkCopied] = useState(false);
  const [actionErrorMessage, setActionErrorMessage] = useState("");
  const [invoiceErrorMessage, setInvoiceErrorMessage] = useState("");
  const [showMissingPaymentInfoDialog, setShowMissingPaymentInfoDialog] =
    useState(false);
  const [missingPaymentCompanyName, setMissingPaymentCompanyName] =
    useState("");
  const [missingInfoType, setMissingInfoType] = useState<
    "driver" | "company" | "payment"
  >("payment");
  const [missingPaymentFields, setMissingPaymentFields] = useState<string[]>(
    [],
  );
  const [missingInfoDetails, setMissingInfoDetails] = useState<string[]>([]);
  const { activeWorkspace } = useWorkspace();
  useEffect(() => {
    setRequirementsTarget(undefined);
  }, [
    openedOrder?.id,
    openedOrder?.status,
    activeWorkspace?.workspaceId,
    activeWorkspace?.role,
  ]);
  // Creating a template is an Owner/Administrator action server-side; hide the
  // "Save as template" order action for everyone else so it never fails on click.
  const canManageTemplates =
    activeWorkspace?.role === WorkspaceRole.Owner ||
    activeWorkspace?.role === WorkspaceRole.Administrator;
  const { updateCacheKey } = useCache();
  const { userSession } = useUserSession();
  const { invalidateOrders, invalidateInvoices } = useDataCache();
  const splitTableRef = useRef<SplitTableRef>(null);

  // Drivers and companies come from the centrally-loaded data store.
  const { drivers, companies } = useWorkspaceData();

  const navigate = useNavigate();
  const location = useLocation();

  const getOrderLabel = (order: Order) =>
    order.orderNumber ? `#${order.orderNumber}` : order.id;

  const getOrderCustomerName = (order: Order) => {
    const customer = order.customer;
    if (!customer) return t("orders:unknown_customer", "unknown customer");
    if (customer.isCompany)
      return customer.name || t("orders:unknown_customer", "unknown customer");
    const name =
      `${customer.firstName || ""} ${customer.lastName || ""}`.trim();
    return (
      name || customer.name || t("orders:unknown_customer", "unknown customer")
    );
  };

  const getInvoiceContextDetails = (
    order: Order,
    extraDetails: string[] = [],
  ) => [
    `${t("common:order_number")}: ${getOrderLabel(order)}`,
    `${t("common:customer")}: ${getOrderCustomerName(order)} (${order.customerId || t("common:missing", "missing")})`,
    ...extraDetails,
  ];

  const getMissingPaymentFields = (paymentTerms: any) => {
    const missingFields: string[] = [];
    if (!paymentTerms.accountNumber || paymentTerms.accountNumber === "") {
      missingFields.push(
        t("orders:payment_field_account_number", "Account number"),
      );
    }
    const hasPaymentDays =
      typeof paymentTerms.paymentDays === "number"
        ? Number.isFinite(paymentTerms.paymentDays)
        : typeof paymentTerms.paymentDays === "string"
          ? (paymentTerms.paymentDays as string).trim() !== ""
          : false;
    if (!hasPaymentDays) {
      missingFields.push(
        t("orders:payment_field_payment_days", "Payment days"),
      );
    }
    return missingFields;
  };

  const sortedOrders = useMemo(() => {
    if (!props.orders || props.orders.length === 0) return [];
    return sortOrders(props.orders, props.sortMode ?? "created-desc");
  }, [props.orders, props.sortMode]);

  // Oppdater searchResults basert på sortedOrders og søketekst
  useEffect(() => {
    if (props.applyLocalSearch !== false && searchText && searchText.length > 0) {
      try {
        // Filtrer de sorterte ordrene basert på søketekst
        const searchLowerCase = searchText.toLowerCase();
        const filteredOrders = sortedOrders.filter((order) => {
          // Build customer name string safely
          const customerName = Customer.getCustomerName(
            order.customer,
          ).toLowerCase();

          // Check for order number safely - Convert to string first
          const orderNumber = String(order.orderNumber || "").toLowerCase();

          // Return true if either customer name or order number matches
          return (
            customerName.includes(searchLowerCase) ||
            orderNumber.includes(searchLowerCase)
          );
        });

        setSearchResults(filteredOrders);
      } catch (error) {
        console.error("Error during search filtering:", error);
        // Fallback to showing all orders if an error occurs during filtering
        setSearchResults(sortedOrders);
      }
    } else {
      // Hvis ingen søketekst, bruk alle sorterte ordrer
      setSearchResults(sortedOrders);
    }
  }, [sortedOrders, searchText, props.applyLocalSearch]);

  // Update searchText when customerSearchText prop changes
  useEffect(() => {
    if (props.customerSearchText !== undefined) {
      setSearchText(props.customerSearchText);
    }
  }, [props.customerSearchText]);

  const updateOpenedOrder = (order: Order | undefined) => {
    if (order !== undefined) {
      // Always update the URL parameters when an order is clicked
      const currentParams = new URLSearchParams(location.search);
      currentParams.set("id", order.id);
      // Add orderNumber if it exists
      if (order.orderNumber) {
        currentParams.set("orderNumber", order.orderNumber);
      } else {
        // Ensure orderNumber is removed if it doesn't exist for this order
        currentParams.delete("orderNumber");
      }

      // Navigate to same page with updated parameters
      navigate(
        {
          pathname: "/orders",
          search: currentParams.toString(),
        },
        { replace: true },
      );

      // Only update the UI state if preventAutoOpen is false
      if (!props.preventAutoOpen) {
        setOpenedOrder(order);
        setHideColumns(true);
        props.isOpen(true);
        splitTableRef.current?.setSplit(true);
      }
    } else {
      // Close split view
      setOpenedOrder(undefined);
      setHideColumns(false);
      props.isOpen(false);
      splitTableRef.current?.setSplit(false);

      // When closing split view, remove id parameter from URL
      const currentParams = new URLSearchParams(location.search);
      currentParams.delete("id");
      // Also remove orderNumber when closing
      currentParams.delete("orderNumber");

      // Navigate to same page with updated parameters
      navigate(
        {
          pathname: "/orders",
          search: currentParams.toString(),
        },
        { replace: true },
      );
    }

    // Notify parent about order selection
    if (props.onOrderSelect) {
      props.onOrderSelect(order);
    }
  };

  const orderId = new URLSearchParams(location.search).get("id");
  const selectedCandidate =
    props.linkedOrder?.id === orderId
      ? props.linkedOrder
      : props.orders.find((order) => order.id === orderId);
  const matchingOrder =
    selectedCandidate?.workspaceId === activeWorkspace?.workspaceId
      ? selectedCandidate
      : undefined;
  const { isOpen: onSplitChange, onOrderSelect } = props;

  useEffect(() => {
    // A direct link can point outside the visible page. Keep details live from
    // the workspace store without inserting that order into filtered table rows.
    // Clear old details when the URL changes to an unavailable order or closes.
    setOpenedOrder(matchingOrder);
    setHideColumns(Boolean(matchingOrder));
    onSplitChange(Boolean(matchingOrder));
    splitTableRef.current?.setSplit(Boolean(matchingOrder));
    onOrderSelect?.(matchingOrder);
  }, [matchingOrder, onSplitChange, onOrderSelect]);

  useEffect(() => {
    if (!props.isLoading && searchResults) {
      setTableRows(
        searchResults.map((order) => {
          return {
            id: order.id,
            isActive: order.id === openedOrder?.id,
            onRowClickData: order,
            cells: [
              {
                id: "orderNumber",
                header: t("common:order_number"),
                children: order.orderNumber ?? "-",
                showOnSmallScreen: true,
              },
              {
                id: "customer",
                children:
                  Customer.getCustomerName(order.customer) ||
                  t("common:unknown_customer"),
                showOnSmallScreen: true,
              },
              {
                id: "status",
                children: (
                  <Inline gap={2}>
                    <StatusBadge status={order.status} />
                    {completeness.data?.[order.id]?.needsAttention && (
                      <button
                        type="button"
                        className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200"
                        title={t("orders:driverOrder.completeData")}
                        onClick={(event) => {
                          event.stopPropagation();
                          updateOpenedOrder(order);
                          requestAnimationFrame(() =>
                            document
                              .getElementById("order-completion")
                              ?.scrollIntoView({
                                block: "start",
                                behavior: "smooth",
                              }),
                          );
                        }}
                      >
                        {t("orders:driverOrder.missingData")}
                      </button>
                    )}
                    {order.isInvoiced && (
                      <Tooltip content={t("orders:completed_orders.invoiced")}>
                        <HiOutlineDocumentText className="h-5 w-5 text-muted" />
                      </Tooltip>
                    )}
                  </Inline>
                ),
                showOnSmallScreen: true,
              },
              {
                id: "date",
                children: Timestamp.toDisplayDate(order.route.at(0)?.stopDate),
              },
              {
                id: "from",
                children: order.route.at(0)?.location?.addressLine ?? "",
                hidden: hideColumns,
              },
              {
                id: "to",
                children: order.route.at(-1)?.location?.addressLine ?? "",
                hidden: hideColumns,
              },
              {
                id: "open",
                children: <HiChevronRight size={20} />,
                hidden: hideColumns,
              },
            ],
          };
        }),
      );
    }
  }, [
    props.isLoading,
    searchResults,
    t,
    openedOrder,
    hideColumns,
    completeness.data,
  ]);

  const cancelOrder = async () => {
    if (openedOrder) {
      setIsLoading(true);
      await cancelOrderApi(
        userSession?.token.accessToken ?? "",
        activeWorkspace?.workspaceId ?? "",
        openedOrder.id,
      ).then((res) => {
        updateOpenedOrder(res);
        invalidateOrders();
        setShowCancelOrderDialog(!showCancelOrderDialog);
      });
      setIsLoading(false);
    }
  };
  const update = async (partial: Partial<IOrder>) => {
    if (openedOrder) {
      setIsLoading(true);
      try {
        // Status transitions are owned by the backend: it updates route
        // documents, cargo statuses and the order aggregate consistently
        // (same lifecycle the driver app uses). The realtime listener and
        // invalidateOrders() bring the result back into the UI, so no
        // Firestore dual-writes are needed here.
        let res;
        if (partial.status === OrderStatus.Completed) {
          res = await completeOrder(
            userSession?.token.accessToken ?? "",
            activeWorkspace?.workspaceId ?? "",
            openedOrder.id,
          );
        } else if (partial.status === OrderStatus.InProgress) {
          res = await startOrder(
            userSession?.token.accessToken ?? "",
            activeWorkspace?.workspaceId ?? "",
            openedOrder.id,
          );
        } else if (partial.status === OrderStatus.Cancelled) {
          res = await cancelOrderApi(
            userSession?.token.accessToken ?? "",
            activeWorkspace?.workspaceId ?? "",
            openedOrder.id,
          );
        } else if (partial.status !== undefined) {
          // No other transition has a backend owner; refuse silent
          // Firestore status patches.
          throw new Error(`Unsupported status transition: ${partial.status}`);
        } else if (partial.driverId !== undefined) {
          res = await assignOrder(
            userSession?.token.accessToken ?? "",
            activeWorkspace?.workspaceId ?? "",
            openedOrder.id,
            partial.driverId,
          );
        } else {
          // Non-status fields (route/cargo edits) still patch Firestore
          // directly until they get a backend endpoint.
          res = await Order.update(openedOrder, partial);
        }
        updateOpenedOrder(res);
        // The Firestore listener picks up the change, but the API-paginated
        // orders list must be invalidated explicitly.
        invalidateOrders();
      } catch (error) {
        console.error("Error updating order:", error);
        const apiError = error as { response?: { data?: { error?: string } } };
        setActionErrorMessage(apiError.response?.data?.error || "");
        setShowActionErrorToast(true);
      } finally {
        setIsLoading(false);
        // Close the status dialog if it initiated the update; never toggle it
        // open from other actions (start/driver/route edits).
        setShowUpdateStatus(false);
      }
    }
  };
  const deleteOrder = async () => {
    if (!openedOrder) return;
    setIsLoading(true);
    try {
      await deleteOrderApi(
        userSession?.token.accessToken ?? "",
        activeWorkspace?.workspaceId ?? "",
        openedOrder.id,
      );
      setShowDeleteOrderDialog(!showDeleteOrderDialog);
      if (splitTableRef.current) {
        splitTableRef.current.setSplit(false); //Close split table
      }
      updateOpenedOrder(undefined);
    } catch (e) {
      console.error("Failed to delete order via API", e);
    } finally {
      setIsLoading(false);
    }
  };
  const sendDeliveryLetter = (email: string) => {
    console.log("Send delivery letter to", email);
  };

  const copyTrackingUrl = async (value: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand("copy");
        textarea.remove();
        return copied;
      } catch {
        return false;
      }
    }
  };

  const createTrackingLink = async () => {
    if (!openedOrder || !userSession || !activeWorkspace) return;
    setIsLoading(true);
    try {
      const link = await createOrderTrackingLink(
        userSession.token.accessToken,
        activeWorkspace.workspaceId,
        openedOrder.id,
      );
      if (!link.token)
        throw new Error("Tracking link response did not include a token");
      const url = buildPublicTrackingUrl(link.token);
      setTrackingUrl(url);
      setTrackingLinkCopied(await copyTrackingUrl(url));
    } catch (error) {
      console.error("Failed to create tracking link", error);
      const apiError = error as {
        response?: { status?: number; data?: { error?: string } };
      };
      const status = apiError.response?.status;
      setActionErrorMessage(
        status === 404
          ? t("orders:tracking_link.not_available")
          : status === 400
            ? t("orders:tracking_link.unsupported_order")
            : apiError.response?.data?.error ||
              t("orders:all_orders.action_failed"),
      );
      setShowTrackingLinkDialog(false);
      setShowActionErrorToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Snapshot the opened order into a reusable template (dates are dropped and
  // per-stop day offsets derived by `OrderTemplate.fromOrder`). The dialog also
  // lets the user attach a recurrence schedule, i.e. turn a past/existing order
  // into a recurring one after the fact.
  const handleSaveTemplateFromOrder = async (
    name: string,
    description: string,
    recurrence: IRecurrenceRule | null,
    projectNumber: string,
  ) => {
    if (!openedOrder || !activeWorkspace) return;
    setSavingTemplate(true);
    try {
      const workspaceId = activeWorkspace.workspaceId;
      const templateData = OrderTemplate.fromOrder(
        { ...(openedOrder as IOrder), workspaceId },
        name,
        description,
      );
      templateData.projectNumber = projectNumber;
      templateData.recurrence = recurrence ?? undefined;

      const created = await OrderTemplate.create(workspaceId, templateData);
      if (!created) {
        throw new Error("Failed to create order template");
      }

      updateCacheKey();
      setShowSaveTemplateDialog(false);
      setShowTemplateSuccessToast(true);
    } catch (e) {
      console.error("Failed to save order template from order", e);
      setShowActionErrorToast(true);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleOpenCreateInvoiceDialog = () => {
    if (!openedOrder) return;
    // Find a valid driver/company pair for this order. Older data can contain
    // several driver documents with the same email, so the first match is not
    // necessarily the one that should be used for invoicing.
    const matchingDrivers = drivers.filter(
      (d) => d.email === openedOrder.driverId,
    );
    if (matchingDrivers.length === 0) {
      setShowMissingPaymentInfoDialog(true);
      setMissingPaymentCompanyName("");
      setMissingInfoType("driver");
      setMissingPaymentFields([]);
      setMissingInfoDetails(
        getInvoiceContextDetails(openedOrder, [
          `Driver: ${openedOrder.driverId || t("common:missing", "missing")}`,
        ]),
      );
      return;
    }

    const driverCompanyMatches = matchingDrivers.map((driver) => ({
      driver,
      company: companies.find((c) => c.id === driver.companyId),
    }));
    const validMatch = driverCompanyMatches.find(
      ({ company }) =>
        company &&
        getMissingPaymentFields(company.paymentTerms || {}).length === 0,
    );
    if (validMatch) {
      setShowCreateInvoiceDialog(true);
      return;
    }

    const firstMatchWithCompany = driverCompanyMatches.find(
      ({ company }) => company,
    );
    if (!firstMatchWithCompany) {
      const companyIds = matchingDrivers.map(
        (driver) => driver.companyId || t("common:missing", "missing"),
      );
      setShowMissingPaymentInfoDialog(true);
      setMissingPaymentCompanyName(companyIds.join(", "));
      setMissingInfoType("company");
      setMissingPaymentFields([]);
      setMissingInfoDetails(
        getInvoiceContextDetails(openedOrder, [
          `Driver: ${openedOrder.driverId}`,
          `Company ID: ${companyIds.join(", ")}`,
        ]),
      );
      return;
    }

    const driver = firstMatchWithCompany.driver;
    const company = firstMatchWithCompany.company;
    if (!company) return;
    // Check payment info
    const missingFields = getMissingPaymentFields(company.paymentTerms || {});
    if (missingFields.length > 0) {
      setShowMissingPaymentInfoDialog(true);
      setMissingPaymentCompanyName(company.name || company.id);
      setMissingInfoType("payment");
      setMissingPaymentFields(missingFields);
      setMissingInfoDetails(
        getInvoiceContextDetails(openedOrder, [
          `Driver: ${driver.email}`,
          `Company ID: ${company.id}`,
        ]),
      );
      return;
    }
    setShowCreateInvoiceDialog(true);
  };

  const handleCreateInvoice = async () => {
    if (!openedOrder) return;
    setIsLoading(true);
    try {
      const response = await createOrderInvoice(
        userSession?.token.accessToken ?? "",
        openedOrder.id,
      );
      setShowInvoiceSuccessToast(true);
      openedOrder.isInvoiced = true;

      // Refresh the invoice list cache so the newly created draft is present
      // when we navigate to the invoices page (otherwise it only appears after
      // a manual page refresh).
      invalidateInvoices();

      // Navigate to the created invoice draft
      if (response?.invoice?.id) {
        navigate(`/invoices?id=${response.invoice.id}`);
      }
    } catch (error) {
      const apiError = error as { response?: { data?: { error?: string } } };
      setInvoiceErrorMessage(
        apiError.response?.data?.error === "DRIVER_ORDER_REVIEW_REQUIRED"
          ? t("orders:driverOrder.invoiceHold")
          : apiError.response?.data?.error || "",
      );
      setShowInvoiceErrorToast(true);
      console.error("Failed to create invoice:", error);
    } finally {
      setIsLoading(false);
      setShowCreateInvoiceDialog(false);
    }
  };

  const orderTableHeaders = hideColumns
    ? [
        {
          id: "orderNumber",
          children: t("common:order_number"),
          showOnSmallScreen: true,
        },
        {
          id: "customer",
          children: t("common:customer"),
          showOnSmallScreen: true,
        },
        {
          id: "date",
          children: t("common:date"),
        },
      ]
    : [
        {
          id: "orderNumber",
          children: t("common:order_number"),
          showOnSmallScreen: true,
        },
        {
          id: "customer",
          children: t("common:customer"),
          showOnSmallScreen: true,
        },
        {
          id: "status",
          children: t("common:status"),
          showOnSmallScreen: true,
        },
        {
          id: "date",
          children: t("common:date"),
        },
        {
          id: "from",
          children: t("common:from"),
        },
        {
          id: "to",
          children: t("common:to"),
        },
        {
          id: "open",
          children: <></>,
        },
      ];

  useEffect(() => {
    if (!props.isLoading && searchResults) {
      setTableRows(
        searchResults.map((order) => {
          // Build the customer name string:
          const customerName =
            Customer.getCustomerName(order.customer) ||
            t("common:unknown_customer");

          return {
            id: order.id,
            isActive: order.id === openedOrder?.id,
            onRowClickData: order,
            cells: hideColumns
              ? [
                  {
                    id: "orderNumber",
                    children: order.orderNumber ?? "-",
                    showOnSmallScreen: true,
                  },
                  {
                    id: "customer",
                    children: customerName,
                    showOnSmallScreen: true,
                  },
                  {
                    id: "date",
                    children: Timestamp.toDisplayDate(
                      order.route.at(0)?.stopDate,
                    ),
                  },
                ]
              : [
                  {
                    id: "orderNumber",
                    children: order.orderNumber ?? "-",
                    showOnSmallScreen: true,
                  },
                  {
                    id: "customer",
                    children: customerName,
                    showOnSmallScreen: true,
                  },
                  {
                    id: "status",
                    children: (
                      <Inline gap={2}>
                        <StatusBadge status={order.status} />
                        {completeness.data?.[order.id]?.needsAttention && (
                          <button
                            type="button"
                            className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200"
                            title={t("orders:driverOrder.completeData")}
                            onClick={(event) => {
                              event.stopPropagation();
                              updateOpenedOrder(order);
                              requestAnimationFrame(() =>
                                document
                                  .getElementById("order-completion")
                                  ?.scrollIntoView({
                                    block: "start",
                                    behavior: "smooth",
                                  }),
                              );
                            }}
                          >
                            {t("orders:driverOrder.missingData")}
                          </button>
                        )}
                        {order.isInvoiced && (
                          <Tooltip
                            content={t("orders:completed_orders.invoiced")}
                          >
                            <HiOutlineDocumentText className="h-5 w-5 text-muted" />
                          </Tooltip>
                        )}
                      </Inline>
                    ),
                    showOnSmallScreen: true,
                  },
                  {
                    id: "date",
                    children: Timestamp.toDisplayDate(
                      order.route.at(0)?.stopDate,
                    ),
                  },
                  {
                    id: "from",
                    children: order?.route[0].location.addressLine || "",
                  },
                  {
                    id: "to",
                    children:
                      order?.route[order.route.length - 1].location
                        .addressLine || "",
                  },
                  {
                    id: "open",
                    children: <HiChevronRight size={20} />,
                  },
                ],
          };
        }),
      );
    }
  }, [
    props.isLoading,
    searchResults,
    t,
    openedOrder,
    hideColumns,
    completeness.data,
  ]);

  // Handle search text change and propagate to parent if needed
  const handleSearchTextChange = (text: string) => {
    setSearchText(text);

    if (props.onCustomerSearch) {
      props.onCustomerSearch(text);
    }

    // Vis lasteindikatoren kort for å gi visuell tilbakemelding
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  };

  const canEditRequirements = Boolean(
    openedOrder &&
    activeWorkspace &&
    [WorkspaceRole.Owner, WorkspaceRole.Administrator].includes(
      activeWorkspace.role,
    ) &&
    [OrderStatus.Draft, OrderStatus.New, OrderStatus.Scheduled].includes(
      openedOrder.status,
    ),
  );

  const canCreateTrackingLink =
    !!openedOrder &&
    !!activeWorkspace &&
    isOrderTrackingEnabledForWorkspace(activeWorkspace.workspaceId) &&
    openedOrder.status !== OrderStatus.Draft &&
    activeWorkspace?.role !== WorkspaceRole.ReadOnly &&
    (openedOrder.route?.filter((stop) => stop.isPickup === false).length ?? 0) >
      0;

  return (
    <>
      {requirementsTarget &&
        canEditRequirements &&
        requirementsTarget.workspaceId === activeWorkspace?.workspaceId &&
        requirementsTarget.orderId === openedOrder?.id && (
          <OrderRequirementsDialog
            key={`${requirementsTarget.workspaceId}-${requirementsTarget.orderId}`}
            workspaceId={requirementsTarget.workspaceId}
            orderId={requirementsTarget.orderId}
            onClose={() => setRequirementsTarget(undefined)}
          />
        )}
      {instructionsTarget &&
        instructionsTarget.workspaceId === activeWorkspace?.workspaceId &&
        instructionsTarget.orderId === openedOrder?.id && (
          <OrderInstructionsDialog
            key={`${instructionsTarget.workspaceId}-${instructionsTarget.orderId}`}
            orderId={instructionsTarget.orderId}
            workspaceId={instructionsTarget.workspaceId}
            onClose={() => setInstructionsTarget(undefined)}
          />
        )}
      {completeness.isError && (
        <button
          type="button"
          className="mb-3 text-sm underline"
          onClick={() => completeness.refetch()}
        >
          {t("orders:driverOrder.checkFailed")}
        </button>
      )}
      <SplitTable
        ref={splitTableRef}
        searchable={false}
        isLoading={props.isLoading}
        tableRows={tableRows}
        tableHeaders={orderTableHeaders}
        tableFooter={props.tableFooter}
        onRowClick={(order: Order | undefined) => updateOpenedOrder(order)}
        updateTable={(val) => handleSearchTextChange(val)}
        content={
          <OrderDetails
            editCargo={() => setShowEditCargo(true)}
            order={openedOrder}
            drivers={drivers}
          />
        }
        topRightContent={
          <div className="flex items-center">
            {openedOrder &&
              (openedOrder.status !== OrderStatus.Cancelled ||
                userSession?.user.isAdmin) && (
                <DropdownButton
                  placement="bottom-end"
                  buttonText={t("orders:actions")}
                  buttonSize="sm"
                  className="h-[44px]"
                  buttonAppendIcon={
                    <HiOutlineChevronDown className="ml-1 sm:ml-2 h-5 w-5 sm:h-4 sm:w-4" />
                  }
                  items={[
                    ...(canEditRequirements
                      ? [
                          {
                            text: (
                              <p className="p-4 w-48">
                                {t("workspace:features.editRequirements")}
                              </p>
                            ),
                            onClick: () =>
                              setRequirementsTarget({
                                orderId: openedOrder.id,
                                workspaceId: activeWorkspace!.workspaceId,
                              }),
                          },
                        ]
                      : []),
                    ...([
                      WorkspaceRole.Owner,
                      WorkspaceRole.Administrator,
                    ].includes(activeWorkspace?.role as WorkspaceRole) &&
                    ![OrderStatus.Completed, OrderStatus.Cancelled].includes(
                      openedOrder.status,
                    )
                      ? [
                          {
                            text: (
                              <p className="p-4 w-48">
                                {t("orders:instructions.title")}
                              </p>
                            ),
                            onClick: () =>
                              setInstructionsTarget({
                                orderId: openedOrder.id,
                                workspaceId: activeWorkspace!.workspaceId,
                              }),
                          },
                        ]
                      : []),
                    ...(canCreateTrackingLink
                      ? [
                          {
                            text: (
                              <p className="p-4 w-48 flex items-center gap-2">
                                <HiOutlineLink className="h-5 w-5" />
                                {t("orders:tracking_link.menu")}
                              </p>
                            ),
                            onClick: () => {
                              setTrackingUrl("");
                              setTrackingLinkCopied(false);
                              setShowTrackingLinkDialog(true);
                            },
                          },
                        ]
                      : []),
                    ...(openedOrder &&
                    [OrderStatus.Draft, OrderStatus.New].includes(
                      openedOrder.status,
                    )
                      ? [
                          {
                            text: (
                              <p className="p-4 w-48">{t("common:edit")}</p>
                            ),
                            onClick: () => {
                              navigate(`/orders/edit/${openedOrder.id}?full=1`);
                            },
                          },
                        ]
                      : []),
                    ...(canManageTemplates
                      ? [
                          {
                            text: (
                              <p className="p-4 w-48">
                                {t("orders:templates.save_as_template_action")}
                              </p>
                            ),
                            onClick: () => {
                              setShowSaveTemplateDialog(true);
                            },
                          },
                        ]
                      : []),
                    ...([OrderStatus.New, OrderStatus.Scheduled].includes(
                      openedOrder?.status,
                    )
                      ? [
                          // Start requires route items, which are created at
                          // confirm — drafts cannot be started.
                          {
                            text: (
                              <p className="p-4 w-48">
                                {t("orders:scheduled_orders.start")}
                              </p>
                            ),
                            onClick: () => {
                              setShowStartOrderDialog(true);
                            },
                          },
                        ]
                      : []),
                    ...([
                      OrderStatus.Draft,
                      OrderStatus.New,
                      OrderStatus.Scheduled,
                    ].includes(openedOrder?.status)
                      ? [
                          // {
                          //   text: (
                          //     <p className="p-4 w-48">
                          //       {t("orders:ongoing_orders.edit_cargo")}
                          //     </p>
                          //   ),
                          //   onClick: () => {
                          //     setShowEditCargo(true);
                          //   },
                          // },
                          // {
                          //   text: (
                          //     <p className="p-4 w-48">
                          //       {t("orders:ongoing_orders.edit_route")}
                          //     </p>
                          //   ),
                          //   onClick: () => {
                          //     setShowEditRoute(true);
                          //   },
                          // },
                          {
                            text: (
                              <p className="p-4 w-48">
                                {openedOrder.driverId
                                  ? t("orders:scheduled_orders.change_driver")
                                  : t("orders:scheduled_orders.add_driver")}
                              </p>
                            ),
                            onClick: () => {
                              setShowEditDriver(true);
                            },
                          },
                        ]
                      : []),
                    ...(openedOrder?.status === OrderStatus.InProgress
                      ? [
                          //{
                          //  text: (
                          //    <p className="p-4 w-48">
                          //      {t("orders:ongoing_orders.edit_cargo")}
                          //    </p>
                          //  ),
                          //  onClick: () => {
                          //    setShowEditCargo(true);
                          //  },
                          //},
                          //{
                          //  text: (
                          //    <p className="p-4 w-48">
                          //      {t("orders:ongoing_orders.edit_route")}
                          //    </p>
                          //  ),
                          //  onClick: () => {
                          //    setShowEditRoute(true);
                          //  },
                          //},
                          // Cancel option moved to be right above delete option
                        ]
                      : []),

                    ...(openedOrder?.status === OrderStatus.Completed
                      ? [
                          // Only show "Create Invoice" if not invoiced
                          ...(!openedOrder?.isInvoiced
                            ? [
                                {
                                  text: (
                                    <p className="p-4 w-48">
                                      {t(
                                        "orders:completed_orders.create_invoice",
                                      )}
                                    </p>
                                  ),
                                  onClick: handleOpenCreateInvoiceDialog,
                                },
                              ]
                            : []),
                          //{
                          //text: (
                          //<p className="p-4 w-48">
                          //{t("orders:completed_orders.download_pdf")}
                          //</p>
                          //),
                          //onClick: () => {
                          //console.log("Download PDF");
                          //},
                          //},
                          //{
                          //text: (
                          //<p className="p-4 w-48">
                          //{t("orders:completed_orders.send_delivery_letter")}
                          //</p>
                          //),
                          //onClick: () => {
                          //setShowDeliveryLetterDialog(true);
                          //},
                          //},
                        ]
                      : []),
                    ...(userSession?.user.isAdmin ||
                    openedOrder?.status === OrderStatus.InProgress
                      ? [
                          {
                            text: (
                              <p className="p-4 w-48">
                                {t("orders:ongoing_orders.update_status")}
                              </p>
                            ),
                            onClick: () => {
                              setShowUpdateStatus(true);
                            },
                          },
                        ]
                      : []),
                    ...(userSession?.user.isAdmin ||
                    [
                      OrderStatus.Draft,
                      OrderStatus.New,
                      OrderStatus.Scheduled,
                      OrderStatus.Completed,
                    ].includes(openedOrder?.status)
                      ? [
                          // For InProgress orders, show Cancel option right before Delete
                          ...(openedOrder?.status === OrderStatus.InProgress
                            ? [
                                {
                                  text: (
                                    <p className="p-4 w-48 text-lgb-red-300 dark:text-lgb-red-200 hover:text-lgb-red-200 hover:dark:text-lgb-red-300">
                                      {t("orders:ongoing_orders.cancel")}
                                    </p>
                                  ),
                                  onClick: () => {
                                    setShowCancelOrderDialog(true);
                                  },
                                },
                              ]
                            : []),
                          {
                            text: (
                              <p className="p-4 w-48 text-lgb-red-300 dark:text-lgb-red-200 hover:text-lgb-red-200 hover:dark:text-lgb-red-300">
                                {t("orders:all_orders.delete")}
                              </p>
                            ),
                            onClick: () => {
                              setShowDeleteOrderDialog(true);
                            },
                          },
                        ]
                      : []),
                  ]}
                />
              )}
          </div>
        }
      />
      {openedOrder && (
        <>
          {drivers.length && (
            <EditDriver
              order={openedOrder}
              drivers={drivers}
              isLoading={isLoading}
              show={showEditDriver}
              type="add"
              edit={(driverId) => {
                update({ driverId: driverId });
              }}
              close={() => setShowEditDriver(false)}
            ></EditDriver>
          )}

          <EditRoute
            route={[...openedOrder.route]}
            show={showEditRoute}
            edit={(route) => {
              update({ route: route });
            }}
            close={() => setShowEditRoute(false)}
          ></EditRoute>

          <EditCargo
            order={openedOrder}
            isLoading={isLoading}
            show={showEditCargo}
            edit={(cargo) => {
              update({ cargo: cargo });
            }}
            close={() => setShowEditCargo(false)}
          ></EditCargo>

          <UpdateStatus
            show={showUpdateStatus}
            isLoading={isLoading}
            currentStatus={openedOrder?.status}
            toggleModal={() => setShowUpdateStatus(!showUpdateStatus)}
            confirmButton={(newStatus: OrderStatus) =>
              update({ status: newStatus })
            }
          />
          <OrderTemplateSaveDialog
            show={showSaveTemplateDialog}
            isLoading={savingTemplate}
            initialName={openedOrder.customer?.name ?? ""}
            initialProjectNumber={openedOrder?.projectNumber}
            onConfirm={handleSaveTemplateFromOrder}
            onCancel={() => setShowSaveTemplateDialog(false)}
          />
          <SendDeliveryLetter
            show={showDeliveryLetterDialog}
            isLoading={isLoading}
            toggleModal={() =>
              setShowDeliveryLetterDialog(!showDeliveryLetterDialog)
            }
            confirmButton={(email: string) => {
              sendDeliveryLetter(email);
              setShowDeliveryLetterDialog(!showDeliveryLetterDialog);
            }}
          />
          <DialogNotify
            show={showTrackingLinkDialog}
            isLoading={isLoading}
            icon={<HiOutlineLink className="h-9 w-9 text-brand" />}
            buttonCloseText={t("common:close")}
            buttonConfirmColor="primaryPurple"
            buttonConfirmPrependIcon={
              trackingUrl ? (
                <HiOutlineClipboardCopy className="mr-2 h-5 w-5" />
              ) : undefined
            }
            buttonConfirmText={
              trackingUrl
                ? t("orders:tracking_link.copy_again")
                : t("orders:tracking_link.create_and_copy")
            }
            title={
              trackingUrl
                ? t("orders:tracking_link.ready_title")
                : t("orders:tracking_link.create_title")
            }
            text={
              trackingUrl
                ? trackingLinkCopied
                  ? t("orders:tracking_link.copied")
                  : t("orders:tracking_link.copy_manually")
                : t("orders:tracking_link.create_description")
            }
            content={
              trackingUrl ? (
                <input
                  aria-label={t("orders:tracking_link.menu")}
                  className="w-full rounded-lg border border-border bg-surface-muted p-3 text-sm text-primary"
                  onFocus={(event) => event.currentTarget.select()}
                  readOnly
                  value={trackingUrl}
                />
              ) : undefined
            }
            toggleModal={() => {
              setShowTrackingLinkDialog(false);
              setTrackingUrl("");
              setTrackingLinkCopied(false);
            }}
            confirmButton={() => {
              if (trackingUrl) {
                void copyTrackingUrl(trackingUrl).then(setTrackingLinkCopied);
              } else {
                void createTrackingLink();
              }
            }}
          />
          <DialogNotify
            show={showStartOrderDialog}
            isLoading={isLoading}
            icon={<StartOrderIcon />}
            buttonCloseText={t("common:close")}
            buttonConfirmColor="primaryPurple"
            buttonConfirmText={t("orders:scheduled_orders.start")}
            title={t("orders:scheduled_orders.start_title")}
            text={t("orders:scheduled_orders.start_description")}
            toggleModal={() => setShowStartOrderDialog(!showStartOrderDialog)}
            confirmButton={() => {
              // The backend starts the first startable route item and sets
              // dates/statuses on the aggregate; no local route mutation.
              update({ status: OrderStatus.InProgress });
              setShowStartOrderDialog(!showStartOrderDialog);
            }}
          />
          <DialogNotify
            show={showDeleteOrderDialog}
            isLoading={isLoading}
            icon={<DeleteOrderIcon />}
            buttonCloseText={t("common:close")}
            buttonConfirmColor="red300"
            buttonConfirmText={t("orders:all_orders.delete")}
            title={t("orders:all_orders.delete_title")}
            text={t("orders:all_orders.delete_description")}
            toggleModal={() => setShowDeleteOrderDialog(!showDeleteOrderDialog)}
            confirmButton={() => {
              deleteOrder();
              setShowDeleteOrderDialog(!showDeleteOrderDialog);
            }}
          />
          <DialogNotify
            show={showCancelOrderDialog}
            isLoading={isLoading}
            icon={<DeleteOrderIcon />}
            buttonCloseText={t("common:close")}
            buttonConfirmColor="red300"
            buttonConfirmText={t("orders:ongoing_orders.cancel")}
            title={t("orders:ongoing_orders.cancel_title")}
            text={t("orders:ongoing_orders.cancel_description")}
            toggleModal={() => setShowCancelOrderDialog(!showCancelOrderDialog)}
            confirmButton={() => {
              cancelOrder();
              setShowCancelOrderDialog(!showCancelOrderDialog);
            }}
          />
          <DialogNotify
            show={showCreateInvoiceDialog}
            isLoading={isLoading}
            icon={<HiCheck className="h-8 w-8 text-brand" />}
            buttonCloseText={t("common:close")}
            buttonConfirmColor="primaryPurple"
            buttonConfirmText={t("orders:completed_orders.create_invoice")}
            title={t("orders:completed_orders.create_invoice")}
            text={t(
              "orders:completed_orders.create_invoice_confirm",
              "Are you sure you want to create an invoice?",
            )}
            toggleModal={() => setShowCreateInvoiceDialog(false)}
            confirmButton={handleCreateInvoice}
          />
          <DialogNotify
            show={showMissingPaymentInfoDialog}
            isLoading={false}
            buttonCloseText={t("common:close")}
            buttonConfirmColor="primaryPurple"
            buttonConfirmText={
              missingInfoType === "driver"
                ? t("orders:go_to_driver_info")
                : t("orders:go_to_company_info")
            }
            title={t("orders:missing_info_title")}
            text={
              missingInfoType === "driver"
                ? t("orders:missing_driver_info")
                : missingInfoType === "company"
                  ? t("orders:missing_company_info", {
                      companyId: missingPaymentCompanyName,
                    })
                  : t("orders:missing_payment_info", {
                      company: missingPaymentCompanyName,
                    })
            }
            content={
              missingInfoDetails.length > 0 ||
              missingPaymentFields.length > 0 ? (
                <ul className="list-disc text-md text-muted">
                  {[...missingInfoDetails, ...missingPaymentFields].map(
                    (field, idx) => (
                      <li key={idx}>{field}</li>
                    ),
                  )}
                </ul>
              ) : null
            }
            toggleModal={() => setShowMissingPaymentInfoDialog(false)}
            confirmButton={() => {
              setShowMissingPaymentInfoDialog(false);
              if (missingInfoType === "driver") {
                if (openedOrder?.driverId) {
                  navigate(
                    `/resources/drivers?driver=${encodeURIComponent(openedOrder.driverId)}`,
                  );
                } else {
                  navigate("/resources/drivers");
                }
              } else {
                if (missingPaymentCompanyName) {
                  navigate(
                    `/workspace/companies?company=${encodeURIComponent(missingPaymentCompanyName)}`,
                  );
                } else {
                  navigate("/workspace/companies");
                }
              }
            }}
          />
        </>
      )}
      {showInvoiceSuccessToast && (
        <div className="fixed top-5 right-5 z-50">
          <Toast className="!bg-green-100 dark:!bg-green-700 !text-green-700 dark:!text-white min-w-[350px] max-w-[450px] shadow-lg">
            <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-500 text-white dark:bg-green-600 dark:text-white">
              <HiCheck className="h-6 w-6" />
            </div>
            <div className="ml-3 text-base font-medium mr-8">
              <p className="text-green-700 dark:text-white">
                {t("orders:completed_orders.invoice_created")}
              </p>
            </div>
            <Toast.Toggle
              onClick={() => setShowInvoiceSuccessToast(false)}
              className="absolute top-2 right-2 bg-transparent text-green-700 hover:text-green-900 rounded-lg focus:ring-2 focus:ring-green-300 p-1.5 hover:bg-green-100/50 inline-flex h-8 w-8 dark:bg-transparent dark:text-white dark:hover:text-white dark:hover:bg-green-700/40"
            />
          </Toast>
        </div>
      )}
      {showTemplateSuccessToast && (
        <div className="fixed top-5 right-5 z-50">
          <Toast className="!bg-green-100 dark:!bg-green-700 !text-green-700 dark:!text-white min-w-[350px] max-w-[450px] shadow-lg">
            <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-500 text-white dark:bg-green-600 dark:text-white">
              <HiCheck className="h-6 w-6" />
            </div>
            <div className="ml-3 text-base font-medium mr-8">
              <p className="text-green-700 dark:text-white">
                {t("orders:templates.toast.created_from_order")}
              </p>
            </div>
            <Toast.Toggle
              onClick={() => setShowTemplateSuccessToast(false)}
              className="absolute top-2 right-2 bg-transparent text-green-700 hover:text-green-900 rounded-lg focus:ring-2 focus:ring-green-300 p-1.5 hover:bg-green-100/50 inline-flex h-8 w-8 dark:bg-transparent dark:text-white dark:hover:text-white dark:hover:bg-green-700/40"
            />
          </Toast>
        </div>
      )}
      {showInvoiceErrorToast && (
        <div className="fixed top-5 right-5 z-50">
          <Toast className="!bg-red-100 dark:!bg-red-700 !text-red-700 dark:!text-white min-w-[350px] max-w-[450px] shadow-lg">
            <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-500 text-white dark:bg-red-600 dark:text-white">
              <HiCheck className="h-6 w-6" />
            </div>
            <div className="ml-3 text-base font-medium mr-8">
              <p className="text-red-700 dark:text-white">
                {invoiceErrorMessage ||
                  t(
                    "orders:completed_orders.invoice_create_failed",
                    "Failed to create invoice",
                  )}
              </p>
            </div>
            <Toast.Toggle
              onClick={() => setShowInvoiceErrorToast(false)}
              className="absolute top-2 right-2 bg-transparent text-red-700 hover:text-red-900 rounded-lg focus:ring-2 focus:ring-red-300 p-1.5 hover:bg-red-100/50 inline-flex h-8 w-8 dark:bg-transparent dark:text-white dark:hover:text-white dark:hover:bg-red-700/40"
            />
          </Toast>
        </div>
      )}
      {showActionErrorToast && (
        <div className="fixed top-5 right-5 z-50">
          <Toast className="!bg-red-100 dark:!bg-red-700 !text-red-700 dark:!text-white min-w-[350px] max-w-[450px] shadow-lg">
            <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-500 text-white dark:bg-red-600 dark:text-white">
              <HiCheck className="h-6 w-6" />
            </div>
            <div className="ml-3 text-base font-medium mr-8">
              <p className="text-red-700 dark:text-white">
                {actionErrorMessage ||
                  t(
                    "orders:all_orders.action_failed",
                    "The action could not be completed",
                  )}
              </p>
            </div>
            <Toast.Toggle
              onClick={() => setShowActionErrorToast(false)}
              className="absolute top-2 right-2 bg-transparent text-red-700 hover:text-red-900 rounded-lg focus:ring-2 focus:ring-red-300 p-1.5 hover:bg-red-100/50 inline-flex h-8 w-8 dark:bg-transparent dark:text-white dark:hover:text-white dark:hover:bg-red-700/40"
            />
          </Toast>
        </div>
      )}
    </>
  );
};
